/**
 * RunningZoneCard
 *
 * One valve that is open right now: what it waters, how long it has been going,
 * and how much longer it has.
 *
 * A scheduled run has all three, so it gets a progress bar. A manual run has
 * only the first — the node reports the valve open but no armed window explains
 * it — and the card says exactly that rather than showing a bar that would be a
 * guess. Being unable to say "how much longer" is real information here: it
 * means nothing is going to close that valve on a timer.
 *
 * A third case belongs to sites whose timers run on a laptop bridge: the window
 * is open now, and nothing has been heard from the node since it opened. The
 * timings are real — the same program the bridge is running — so the card keeps
 * them and marks the one thing genuinely unknown, rather than dropping the run
 * or dressing it up as water on the ground. A valve the node has actually
 * reported closed inside its window never gets a card: that program did not
 * fire, and it is raised as an alert instead.
 */

import { useNavigate } from 'react-router-dom';
import { Droplets, MapPin, Hand, HelpCircle, WifiOff } from 'lucide-react';
import { formatRunTime, type ZoneRun } from '../../utils/irrigationRuns';

interface RunningZoneCardProps {
  run: ZoneRun;
  /** Farm names are noise when the page is already filtered to one farm. */
  showFarm?: boolean;
}

export default function RunningZoneCard({ run, showFarm = true }: RunningZoneCardProps) {
  const navigate = useNavigate();
  const scheduled = run.source === 'schedule' && run.remainingSec != null;
  const pct = run.progress != null ? Math.round(run.progress * 100) : null;

  return (
    <button
      type="button"
      onClick={() => navigate(`/irrigation/espnow/${run.controllerId}`)}
      className={`group w-full rounded-xl border bg-white p-4 text-left shadow-sm transition hover:shadow-md ${
        run.confirmed
          ? 'border-blue-200 hover:border-blue-300'
          : 'border-dashed border-gray-300 hover:border-gray-400'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <span
            className="mt-0.5 h-8 w-1.5 shrink-0 rounded-full"
            style={{ backgroundColor: run.zoneColor }}
          />
          <div className="min-w-0">
            <p className="truncate font-semibold text-gray-900">{run.zoneName}</p>
            <p className="truncate text-xs text-gray-500">
              {showFarm && `${run.farmName} · `}
              {run.routerName}
            </p>
          </div>
        </div>

        <div className="shrink-0 text-right">
          {scheduled ? (
            <>
              <p
                className={`text-lg font-bold leading-tight tabular-nums ${
                  run.confirmed ? 'text-blue-600' : 'text-gray-600'
                }`}
              >
                {formatRunTime(run.remainingSec!)}
              </p>
              <p className="text-[11px] uppercase tracking-wide text-gray-400">remaining</p>
            </>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-1 text-[11px] font-semibold text-amber-700">
              <Hand className="h-3 w-3" />
              Manual
            </span>
          )}
        </div>
      </div>

      {scheduled ? (
        <>
          <div
            className={`mt-3 h-2 w-full overflow-hidden rounded-full ${
              run.confirmed ? 'bg-blue-100' : 'bg-gray-200'
            }`}
          >
            <div
              className={`h-full rounded-full transition-[width] duration-1000 ease-linear ${
                run.confirmed ? 'bg-blue-500' : 'bg-gray-400'
              }`}
              style={{ width: `${pct ?? 0}%` }}
            />
          </div>
          <div className="mt-1.5 flex items-center justify-between text-xs text-gray-500 tabular-nums">
            <span className="inline-flex items-center gap-1">
              <Droplets
                className={`h-3.5 w-3.5 ${run.confirmed ? 'text-blue-500' : 'text-gray-400'}`}
              />
              {run.confirmed ? 'Running' : 'Due'} {formatRunTime(run.elapsedSec ?? 0)}
            </span>
            <span>of {formatRunTime(run.durationSec ?? 0)}</span>
          </div>
        </>
      ) : (
        <p className="mt-3 text-xs text-gray-500">
          Open with no timed program running — it will stay on until something turns it off.
        </p>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-400">
        {!run.confirmed && (
          <span className="inline-flex items-center gap-1 text-gray-500">
            <HelpCircle className="h-3 w-3" />
            {run.sequenceStarted
              ? 'Started by the site — valve not reported open yet'
              : 'Scheduled now — no valve report since it was due'}
          </span>
        )}
        {!run.boundary && (
          <span className="inline-flex items-center gap-1">
            <MapPin className="h-3 w-3" />
            No area drawn
          </span>
        )}
        {/* Only an earned "offline" — the node was asked and did not answer.
            A node nobody has asked lately is `unknown`, which is no fault, and
            the device pages do not draw it as one either. */}
        {run.routerState === 'offline' && (
          <span className="inline-flex items-center gap-1 text-amber-600">
            <WifiOff className="h-3 w-3" />
            Node did not answer — state may be stale
          </span>
        )}
      </div>
    </button>
  );
}
